import {
  FOC_TOUR_LABEL_PLANNING_PREP_MEETING,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import {
  FOC_REQUIRED_DISPOSITION,
  focPlanningPRequirement,
  focPlanningPTourNavVisible,
  type FocPlanningPTourStepDef,
} from './focPlanningPTourTypes';
import { FOC_PLANNING_PREP_TOUR_HREF } from './focPlanningPTourRegistry';

export { FOC_PLANNING_PREP_TOUR_HREF, focPlanningPTourNavVisible };

/**
 * Unified 11.1–11.2 walk: Prepare Planning (index 4) then Planning Meeting (index 5).
 */
export const FOC_PLANNING_PREP_TOUR_STEPS: FocPlanningPTourStepDef[] = [
  {
    id: 'sectionIntro',
    phaseIndex: 4,
    title: '11.1–11.2 Prep for Planning & Planning Meeting',
    description:
      'Prep for Planning is a dedicated working phase that coordinates next-period products before the Planning Meeting. The Planning Meeting is the IMH-aligned facilitation where Command reviews the plan for the next operational period.',
  },
  focPlanningPRequirement({
    id: 'plan001',
    requirementId: 'FOC-PLAN-001',
    name: 'Deliverable-Driven Planning Workspace',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 4,
    tour: 'planning-forms',
    widget: 'assignmentList',
    description:
      'PRATUS provides a dedicated Prep for Planning Meeting workspace that supports preparation and coordination of the products required for the Planning Meeting — including ICS 204, tactics and resource worksheets, open actions, and support forms.',
  }),
  focPlanningPRequirement({
    id: 'plan001a',
    requirementId: 'FOC-PLAN-001a',
    name: 'Planning Pulse',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 4,
    tour: 'planning-stepper',
    description:
      'The Planning-P stepper rail acts as the Planning Pulse for the Planning Section Chief and authorized leaders: required forms, IAP-related components, owners via form links, assignment and product readiness, review/signature status, blockers visible as incomplete work, and dependencies across safety, logistics, finance, and resource-request products. A separate Pulse dashboard is not required for this walkthrough.',
  }),
  focPlanningPRequirement({
    id: 'plan001b',
    requirementId: 'FOC-PLAN-001b',
    name: 'Readiness Status',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 4,
    description:
      'The workspace distinguishes Readiness through stepper form status dots and version/signature indicators — covering Not Started, In Progress, Ready for Review, Ready for the Planning Meeting, Needs Revision, Approved, and other authorized statuses as products mature.',
  }),
  focPlanningPRequirement({
    id: 'plan001c',
    requirementId: 'FOC-PLAN-001c',
    name: 'Cross-Functional Workspaces',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 4,
    tour: 'ics204-card',
    widget: 'assignmentList',
    description:
      'Prep for Planning tracks outputs from Command Staff, Operations, Logistics, Finance/Admin, Safety, Public Information, Liaison, Facilities, Resources, Communications, Medical, Environmental, Intelligence and Investigations, and other authorized contributors. Planning P coordinates those products; it is not the sole location where each function performs its work.',
  }),
  {
    id: 'plan001c215',
    phaseIndex: 4,
    tour: 'ics215-card',
    widget: 'operationalPlanningWorksheet',
    title: 'Cross-Functional — ICS 215',
    description:
      'Resource planning (ICS 215) remains available from Prep for Planning so Operations, Logistics, and Finance dependencies stay visible while preparing for the Planning Meeting.',
  },
  {
    id: 'plan001c233',
    phaseIndex: 4,
    tour: 'ics233-card',
    widget: 'openActionsTracker',
    title: 'Cross-Functional — Open Actions',
    description:
      'ICS 233 open actions carry follow-on work from Command and Section contributors without forcing all work into a single form.',
  },
  {
    id: 'sec112',
    phaseIndex: 5,
    title: '11.2 Planning Meeting',
    description:
      'The Planning Meeting is distinct from Prep for Planning: it is an IMH-aligned facilitation where Incident Command or Unified Command reviews the assembled plan for the next operational period.',
  },
  focPlanningPRequirement({
    id: 'plan002',
    requirementId: 'FOC-PLAN-002',
    name: 'Planning Meeting Workspace',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 5,
    tour: 'planning-agenda',
    showAgenda: true,
    description:
      'PRATUS provides an IMH-aligned Planning Meeting workspace where Incident Command or Unified Command reviews objectives, safety, tactics, organization, resource needs, ICS 215, ICS 215A, logistics, finance/burn-rate information, constraints, and other command decisions for the next operational period.',
  }),
  focPlanningPRequirement({
    id: 'plan002a',
    requirementId: 'FOC-PLAN-002a',
    name: 'Briefing Product Access',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 5,
    tour: 'ics202-card',
    widget: 'incidentObjectives',
    description:
      'The meeting workspace provides rapid access to approved or draft briefing material needed for the Planning Meeting — including objectives (ICS 202), map/COP products from Hub surfaces, tactics (ICS 234), organization, safety, resources (ICS 215), logistics, finance, and applicable support plans via the phase form rail.',
  }),
  {
    id: 'plan002a234',
    phaseIndex: 5,
    tour: 'ics234-card',
    widget: 'workAnalysisMatrix',
    title: 'Briefing Products — Tactics',
    description:
      'ICS 234 strategies and tactics open directly from the Planning Meeting rail so facilitators can review operational products without leaving the meeting context.',
  },
  focPlanningPRequirement({
    id: 'plan002b',
    requirementId: 'FOC-PLAN-002b',
    name: 'Command Decision Record',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 5,
    tour: 'planning-meeting-details',
    openMeetingDetails: true,
    showAgenda: true,
    description:
      'PRATUS records plan approval context, conditional approval or required changes, decision notes, approving authority, timestamp, operational period, and follow-up actions through Meeting Details, agenda notes, and ICS 233 open actions. This walkthrough does not create a new approval record.',
  }),
  focPlanningPRequirement({
    id: 'plan002c',
    requirementId: 'FOC-PLAN-002c',
    name: 'Plan Approval Gate',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 5,
    description:
      'PRATUS identifies whether the plan is approved, conditionally approved, awaiting revision, or not approved through meeting outcomes and product signature/readiness states. A proposed plan is not represented as approved before authorized command approval occurs. This walkthrough does not approve or sign the plan.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_PLANNING_PREP_MEETING),
  },
];

export function focPlanningPrepTourStepIds(): string[] {
  return FOC_PLANNING_PREP_TOUR_STEPS.map((step) => step.id);
}
